
// src/components/Tooltip.js
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const Tooltip = ({ content, children, className = "" }) => {
  const triggerRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, placeBelow: false });

  const tooltipWidth = 340;
  const margin = 10;

  const updatePosition = () => {
    const el = triggerRef.current;
    if (!el) return;

    const rect = el.getBoundingClientRect();

    // Horizontal clamp (viewport coords)
    let left = rect.left;
    const maxLeft = window.innerWidth - tooltipWidth - margin;
    left = Math.min(Math.max(left, margin), maxLeft);

    // Prefer above; if too close to top, place below
    const placeBelow = rect.top < 90; // simple heuristic
    const top = placeBelow ? rect.bottom + 8 : rect.top - 8;

    setPos({ top, left, placeBelow });
  };

  useEffect(() => {
    if (!open) return;

    updatePosition();
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);

    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [open]);

  const tooltipNode = open
    ? createPortal(
        <div
          className="fixed z-[9999] max-w-[340px] rounded-lg bg-gray-900 p-3 text-sm text-gray-100 shadow-lg pointer-events-none"
          style={{
            top: pos.top,
            left: pos.left,
            transform: pos.placeBelow ? "translateY(0)" : "translateY(-100%)",
          }}
          role="tooltip"
        >
          {content}
        </div>,
        document.body
      )
    : null;

  return (
    <>
      <span
        ref={triggerRef}
        className={`inline-flex ${className}`}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        tabIndex={0}
        aria-label="Show definition"
      >
        {children}
      </span>
      {tooltipNode}
    </>
  );
};

export default Tooltip;



